import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { StoreProvider, ThemeProvider } from '@dreamtree-org/twreact-ui';
import '@dreamtree-org/twreact-ui/dist/index.css';
import './index.css';
// After index.css so these defaults win over the twreact-ui preset's :root base vars.
import './theme/tokens.css';
import App from './App.jsx';
import PlatformApp from './platform/PlatformApp.jsx';
import StorefrontApp from './storefront/StorefrontApp.jsx';
import ServiceApp from './service/ServiceApp.jsx';
import VendorApp from './vendor/VendorApp.jsx';
import UnknownHost from './pages/UnknownHost.jsx';
import { restoreBrand } from './theme/applyTheme';
import { startPwaUpdates } from './lib/pwa';
import { setSsoConfig } from './lib/dreamtreeSso';

// Apply any saved per-tenant brand before first paint (runtime theming).
restoreBrand();

/**
 * One build, five surfaces — picked by the HOST the API resolved, then the path:
 *   platform admin host          → Super Admin console        (platform/PlatformApp)
 *   merchant domain /admin/*     → merchant back office       (App, basename /admin)
 *   merchant domain /service/*   → service / repair portal    (service/ServiceApp, basename /service)
 *   merchant domain /vendor/*    → in-store vendor portal     (vendor/VendorApp, basename /vendor, B17)
 *   merchant domain, elsewhere   → the merchant's storefront   (storefront/StorefrontApp)
 */
async function boot() {
  let ctx = null;
  let error = null;
  try {
    const res = await fetch('/api/meta/context', { credentials: 'include' });
    const body = await res.json();
    if (res.ok) ctx = body;
    else error = body;
  } catch (e) {
    error = { error: e.message };
  }

  // "Continue with Dreamtree" on the staff sign-in (DTID-44): present only outside AUTH_MODE=local.
  setSsoConfig(ctx && ctx.sso);

  const under = (base) => window.location.pathname === base || window.location.pathname.startsWith(`${base}/`);
  let surface;
  if (!ctx) surface = <UnknownHost error={error} />;
  else if (ctx.realm === 'platform') surface = <BrowserRouter><PlatformApp /></BrowserRouter>;
  else if (under('/admin')) surface = <BrowserRouter basename="/admin"><App store={ctx.store} /></BrowserRouter>;
  else if (under('/service')) surface = <BrowserRouter basename="/service"><ServiceApp store={ctx.store} /></BrowserRouter>;
  else if (under('/vendor')) surface = <BrowserRouter basename="/vendor"><VendorApp store={ctx.store} /></BrowserRouter>;
  else surface = <BrowserRouter><StorefrontApp store={ctx.store} /></BrowserRouter>;

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <StoreProvider>
        <ThemeProvider>{surface}</ThemeProvider>
      </StoreProvider>
    </React.StrictMode>
  );
}

boot();
startPwaUpdates();
